// Import React hooks and components needed for authentication
import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
// Import TypeScript type definitions for authentication-related data
import { AuthState, AuthContextValue, User, UserRole, RolePermissions } from '../types';
// Import service that handles login/logout API calls
import { authService } from '../services/authService';
// Import service that handles saving data to browser's local storage
import { storageService } from '../services/storageService';
// Import cache clearing function
import { clearApiServiceCache } from '../hooks/useUnifiedApi';
import { productService } from '../services/productService';

/**
 * Initial Authentication State
 * This is what the auth system looks like when the app first starts
 * Everything starts as "unknown" until we check if user is logged in
 */
const initialState: AuthState = {
  user: null,              // No user logged in yet
  isAuthenticated: false,  // User is not authenticated yet
  isLoading: true,         // We're checking if user is already logged in
  error: null,             // No error messages yet
  isLoggingOut: false,     // Whether logout is in progress
};

/**
 * AuthAction Type Definition
 * These are all the different "events" that can happen in the authentication system
 * Think of them like commands: "start login", "login succeeded", "login failed", etc.
 */
type AuthAction =
  | { type: 'AUTH_START' }                        // User clicked login, starting the process
  | { type: 'AUTH_SUCCESS'; payload: User }       // Login succeeded, here's the user data
  | { type: 'AUTH_FAILURE'; payload: string }     // Login failed, here's the error message
  | { type: 'LOGOUT_START' }                      // User clicked logout, starting the process
  | { type: 'AUTH_LOGOUT' }                       // User logged out or session expired
  | { type: 'CLEAR_ERROR' };                      // Clear any error messages

/**
 * authReducer Function
 * This is like the "brain" of the authentication system
 * It takes the current state and an action, then returns the new state
 *
 * This pattern is called "Redux" - it makes state changes predictable and debuggable
 */
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    // When login process starts (user clicked "Sign In")
    case 'AUTH_START':
      return {
        ...state,           // Keep everything the same, but change:
        isLoading: true,    // Show loading spinner
        error: null,        // Clear any previous error messages
      };

    // When login succeeds
    case 'AUTH_SUCCESS':
      return {
        ...state,
        user: action.payload,      // Store the user information
        isAuthenticated: true,     // Mark user as logged in
        isLoading: false,          // Stop showing loading spinner
        error: null,               // Clear any error messages
      };

    // When login fails
    case 'AUTH_FAILURE':
      return {
        ...state,
        user: null,                // No user data since login failed
        isAuthenticated: false,    // User is not logged in
        isLoading: false,          // Stop loading spinner
        error: action.payload,     // Show the error message
      };

    // When logout process starts (user clicked "Sign Out")
    case 'LOGOUT_START':
      return {
        ...state,
        isLoggingOut: true,        // Show logout loading spinner
        error: null,               // Clear any previous error messages
      };

    // When user logs out
    case 'AUTH_LOGOUT':
      return {
        ...state,
        user: null,                // Remove user data
        isAuthenticated: false,    // User is no longer authenticated
        isLoading: false,          // Not loading anything
        isLoggingOut: false,       // Stop showing logout loading spinner
        error: null,               // Clear errors
      };

    // When user dismisses an error message
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,               // Just clear the error, keep everything else
      };

    // If we get an action type we don't recognize, don't change anything
    default:
      return state;
  }
};

// Create the React Context - this is like a "global state" that any component can access
// Think of it like a box that holds authentication info, available anywhere in the app
const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/**
 * useAuth Hook
 * This is a custom hook that components use to access authentication functions
 * Example: const { user, login, logout } = useAuth();
 *
 * It includes safety checks to make sure it's used correctly
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  // Safety check: make sure this hook is used inside an AuthProvider
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * getRolePermissions Function
 * This determines what each type of user is allowed to do in the application
 * It's like a security system that controls access to different features
 *
 * Think of it like different levels of access cards in a building:
 * - Admin: Can access everything (like a master key)
 * - Operator: Can do daily work but not administrative tasks
 * - Viewer: Can only look at things, not change anything
 */
export const getRolePermissions = (role: UserRole): RolePermissions => {
  switch (role) {
    case 'admin':
      // Admin users can do everything - they have full control
      return {
        canViewProducts: true,    // Can see product lists
        canEditProducts: true,    // Can modify product information
        canMatchTags: true,       // Can associate RFID tags with products
        canManageTags: true,      // Can edit and delete tag associations
        canViewReports: true,     // Can see analytics and reports
        canManageUsers: true,     // Can create/edit other user accounts
      };
    case 'operator':
      // Operators can do day-to-day work but not administrative tasks
      return {
        canViewProducts: true,    // Can see products (needed for their work)
        canEditProducts: false,   // Cannot modify product data
        canMatchTags: true,       // Can tag items (this is their main job)
        canManageTags: false,     // Cannot edit existing tag matches
        canViewReports: false,    // Cannot see business reports
        canManageUsers: false,    // Cannot manage other users
      };
    case 'viewer':
      // Viewers can only look at things, not change anything
      return {
        canViewProducts: true,    // Can see product information
        canEditProducts: false,   // Cannot change anything
        canMatchTags: false,      // Cannot create new tag associations
        canManageTags: false,     // Cannot edit tag associations
        canViewReports: true,     // Can see reports (read-only access)
        canManageUsers: false,    // Cannot manage users
      };
    default:
      // For unknown roles or logged-out users, deny everything
      return {
        canViewProducts: false,
        canEditProducts: false,
        canMatchTags: false,
        canManageTags: false,
        canViewReports: false,
        canManageUsers: false,
      };
  }
};

interface AuthProviderProps {
  children: ReactNode;
}

const newrelic = window.newrelic;
if (newrelic) {
  //newrelic.setCustomAttribute('user-id', 'user-1234-v1.0');
  // newrelic.setUserId('user-1234-v1.0');
  console.log('newrelic WAS found');
}
else{
  console.log('newrelic not found');
}


export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const storedUser = storageService.getItem<User>('currentUser');
        const storedToken = storageService.getItem<string>('authToken');
        const storedSmartPermissions = storageService.getItem<any>('smartPermissions');
        const storedSWSPermissions = storageService.getItem<any>('swsMenuPermissions');

        if (storedUser && storedToken) {
          newrelic?.setUserId(storedUser.email);
          console.log('newrelic user set to ', storedUser.email);

          // Restore permission data if available
          if (storedSmartPermissions && storedUser.system === 'smart') {
            // storedPermissions could be menu array or role object
            if (Array.isArray(storedSmartPermissions)) {
              storedUser.rawPermissionData = { menu: storedSmartPermissions };
            } else {
              storedUser.rawPermissionData = { role: storedSmartPermissions };
            }
          } else if (storedSWSPermissions && storedUser.system === 'sws') {
            // Restore SWS menu permissions
            storedUser.rawPermissionData = { menuPermissions: storedSWSPermissions };
          }

          // For mock API tokens, just check if they exist and look valid
          if (storedToken.startsWith('smart_token_') || storedToken.startsWith('sws_token_')) {
            dispatch({ type: 'AUTH_SUCCESS', payload: storedUser });
            return;
          }

          // For real tokens, validate them
          const isValid = await authService.validateToken(storedToken);
          if (isValid) {
            dispatch({ type: 'AUTH_SUCCESS', payload: storedUser });
            return;
          }
        }

        storageService.removeItem('currentUser');
        storageService.removeItem('authToken');
        storageService.removeItem('smartPermissions');
        storageService.removeItem('swsMenuPermissions');
        dispatch({ type: 'AUTH_LOGOUT' });
      } catch (error) {
        console.error('Auth initialization error:', error);
        dispatch({ type: 'AUTH_LOGOUT' });
      }
    };

    initializeAuth();
  }, []);

  const login = async (companyCode: string, username: string, password: string): Promise<void> => {
    dispatch({ type: 'AUTH_START' });

    // Clear caches before login to ensure fresh data for new user
    clearApiServiceCache();
    productService.clearCache();

    try {
      console.log('🔐 AuthContext: Starting real API login...', username);

      // Use real authService (tries both Smart and SWS APIs)
      const response = await authService.login(username, password, companyCode);

      if (response.success && response.user && response.token) {
        console.log('✅ AuthContext: Real API login successful', response.user.system);

        // Store user data and tokens
        storageService.setItem('currentUser', response.user);
        storageService.setItem('authToken', response.token);
        newrelic?.setUserId(response.user.email);
        console.log('newrelic user set to ', response.user.email);

        // Store system-specific tokens if they exist
        if (response.user.system === 'smart') {
          storageService.setItem('smartAccessToken', response.token);
        } else if (response.user.system === 'sws') {
          storageService.setItem('swsAccessToken', response.token);
        }

        dispatch({ type: 'AUTH_SUCCESS', payload: response.user });
      } else {
        console.log('❌ AuthContext: Real API login failed', response.error);
        dispatch({ type: 'AUTH_FAILURE', payload: response.error || 'Login failed' });
      }
    } catch (error: any) {
      console.error('💥 AuthContext: Login error', error);
      dispatch({ type: 'AUTH_FAILURE', payload: error.message || 'Network error. Please try again.' });
    }
  };


  const logout = async (): Promise<void> => {
    dispatch({ type: 'LOGOUT_START' });

    try {
      // Call API logout first (authService handles clearing its own storage)
      await authService.logout();
    } catch (error) {
      console.error('⚠️ API logout failed, proceeding with local cleanup:', error);
    }

    // Clear all authentication data
    storageService.removeItem('currentUser');
    storageService.removeItem('authToken');
    storageService.removeItem('smartAccessToken');
    storageService.removeItem('swsAccessToken');
    storageService.removeItem('swsAppId');
    storageService.removeItem('smartPermissions');
    storageService.removeItem('swsMenuPermissions');

    // Clear all API service caches to prevent data leakage between users
    clearApiServiceCache();
    productService.clearCache();

    dispatch({ type: 'AUTH_LOGOUT' });
    console.log('🚪 AuthContext: User logged out - all tokens and caches cleared');
  };

  const clearError = (): void => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  const value: AuthContextValue = {
    ...state,
    login,
    logout,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Pure permission-mapping function.
 *
 * (2026-04-24, Jason Cox) Extracted from usePermissions so the branch logic
 * can be unit-tested without needing a React render or an AuthProvider
 * wrapper. The hook is now a thin shim: it calls useAuth() and defers to
 * this function.
 */
export const derivePermissions = (user: User | null): RolePermissions => {
  // If user has raw permission data from Smart API, use it
  if (user?.rawPermissionData && user.system === 'smart') {
    try {
      let hasTagItems = false;
      let hasManageTag = false;
      let hasUpdateQuantity = false;
      let hasClearTagData = false;
      // (2026-04-22, Jason Cox) Device Status gate -- mirror Vue admin which
      // uses md_smartbox to show smartbox-related views.
      let hasMdSmartbox = false;

      // Check menu structure (new format)
      if (user.rawPermissionData.menu && Array.isArray(user.rawPermissionData.menu)) {
        const allMenuItems: any[] = [];

        const flattenMenu = (items: any[]) => {
          items.forEach(item => {
            allMenuItems.push(item);
            if (item.children && Array.isArray(item.children)) {
              flattenMenu(item.children);
            }
          });
        };

        flattenMenu(user.rawPermissionData.menu);

        hasTagItems = allMenuItems.some(item => item.menuCode === 'tag_items');
        hasManageTag = allMenuItems.some(item => item.menuCode === 'manage_tag');
        hasMdSmartbox = allMenuItems.some(item => item.menuCode === 'md_smartbox');

        // Check md_rfid permissions with specific actions
        const mdRfidItem = allMenuItems.find(item => item.menuCode === 'md_rfid');
        const mdRfidActions = mdRfidItem?.action ? mdRfidItem.action.split(',').map((a: string) => a.trim()) : [];
        hasUpdateQuantity = mdRfidActions.includes('edit_quantity');
        hasClearTagData = mdRfidActions.includes('delete');

        console.log('📊 Dashboard Permission Evaluation (menu):', {
          hasTagItems,
          hasManageTag,
          hasUpdateQuantity,
          hasClearTagData,
          mdRfidActions,
          menuCodes: allMenuItems.map(item => item.menuCode)
        });
      }

      // ALSO check role.permissions (not just as fallback - user might have both!)
      if (user.rawPermissionData.role?.permissions) {
        const permissions = user.rawPermissionData.role.permissions;
        // Only update if not already found in menu
        hasTagItems = hasTagItems || permissions.some((p: any) => p.menuCode === 'tag_items');
        hasManageTag = hasManageTag || permissions.some((p: any) => p.menuCode === 'manage_tag');
        hasMdSmartbox = hasMdSmartbox || permissions.some((p: any) => p.menuCode === 'md_smartbox');

        // Check md_rfid with actions
        const mdRfidPerm = permissions.find((p: any) => p.menuCode === 'md_rfid');
        const mdRfidActions = mdRfidPerm?.action ? mdRfidPerm.action.split(',').map((a: string) => a.trim()) : [];
        hasUpdateQuantity = hasUpdateQuantity || mdRfidActions.includes('edit_quantity');
        hasClearTagData = hasClearTagData || mdRfidActions.includes('delete');

        console.log('📊 Dashboard Permission Evaluation (role.permissions):', {
          hasTagItems,
          hasManageTag,
          hasUpdateQuantity,
          hasClearTagData,
          permissionCodes: permissions.map((p: any) => p.menuCode)
        });
      }

      // Determine if user should see Manage Tags button (ANY manage permission)
      // Note: tag_items is NOT a manage permission - it's for the Tag Items button
      const canSeeManageTags = hasManageTag || hasUpdateQuantity || hasClearTagData;

      // (2026-07-24, Jason Cox) Industry 3 customers are admitted to the PWA
      // without PDA (tag) permissions -- see SmartAuthProvider.checkPermissions.
      // They get every feature EXCEPT Tag Items and Manage Tags, so the non-tag
      // views below must not be gated on hasTagItems for them. Tag Items
      // (canMatchTags) and Manage Tags (canManageTags) intentionally stay on
      // the real tag permissions, so they remain hidden for these users.
      const industryId = (user.rawPermissionData as any).industryId;
      const isIndustry3 = industryId === 3 || industryId === '3';

      return {
        canViewProducts: true,  // Always allow viewing products
        canEditProducts: false, // PWA doesn't edit products
        canMatchTags: hasTagItems,  // Based on "tag_items" menuCode (Tag Items button)
        canManageTags: canSeeManageTags,  // Show button if ANY manage permission exists
        canViewReports: false,  // Reports not available in PWA
        canManageUsers: false,  // User management not available in PWA
        // Granular permissions for EditTagModal
        canUpdateQuantity: hasUpdateQuantity,  // md_rfid with edit_quantity
        canChangeProduct: hasManageTag,        // manage_tag covers Change Product Association
        canClearTagData: hasClearTagData,      // md_rfid with delete
        canReplaceTag: hasManageTag,           // manage_tag covers Replace RFID Tag
        // (2026-04-16, Jason Cox) Manual Checkout: mirror tag_items for now
        // (no dedicated prod permission key for it yet).
        // (2026-07-24) Also allow industry-3 users (no tag_items by design).
        canManualCheckout: hasTagItems || isIndustry3,
        // (2026-04-22, Jason Cox) Device Status: prefer md_smartbox, but fall
        // back to tag_items so users who can see tags can also see device
        // health. This mirrors the Manual Checkout pattern while still
        // letting admins grant md_smartbox explicitly to gate it narrower.
        canViewDeviceStatus: hasMdSmartbox || hasTagItems || isIndustry3,
        // (2026-04-22, Jason Cox) Consumption Events (delivery tickets):
        // mirror Manual Checkout.
        canViewDeliveryTickets: hasTagItems || isIndustry3,
        // (2026-04-23, Jason Cox) Restock Events (transfer tickets): same gate.
        canViewRestockEvents: hasTagItems || isIndustry3,
        // (2026-04-24, Jason Cox) Inventory view: same gate.
        canViewInventory: hasTagItems || isIndustry3,
      };
    } catch (error) {
      console.error('Error parsing Smart permissions, falling back to role-based:', error);
    }
  }

  // Check SWS permissions
  if (user?.rawPermissionData?.menuPermissions && user.system === 'sws') {
    try {
      const { mobile, web } = user.rawPermissionData.menuPermissions;

      // Flatten mobile menu structure
      const allMobileItems: any[] = [];
      const flattenMenu = (items: any[]) => {
        items.forEach(item => {
          allMobileItems.push(item);
          if (item.children && Array.isArray(item.children)) {
            flattenMenu(item.children);
          }
        });
      };

      if (Array.isArray(mobile)) {
        flattenMenu(mobile);
      }

      // Check mobile permissions
      const hasTagItems = allMobileItems.some(item => item.menuCode === 'tag_items');
      const hasManageTags = allMobileItems.some(item => item.menuCode === 'manage_tags');

      // Check web permissions for md_tags with actions
      const mdTagsItem = Array.isArray(web) ?
        web.find(item => item.menuCode === 'md_tags') : null;
      const hasUpdateQuantity = mdTagsItem?.action?.some((a: any) => a.actionCode === 'edit') || false;
      const hasClearTagData = mdTagsItem?.action?.some((a: any) => a.actionCode === 'delete') || false;

      console.log('📊 SWS Dashboard Permission Evaluation:', {
        hasTagItems,
        hasManageTags,
        hasUpdateQuantity,
        hasClearTagData,
        mobileMenuCodes: allMobileItems.map(item => item.menuCode),
        webActions: mdTagsItem?.action?.map((a: any) => a.actionCode)
      });

      // Determine if user should see Manage Tags button (ANY manage permission)
      const canSeeManageTags = hasManageTags || hasUpdateQuantity || hasClearTagData;

      return {
        canViewProducts: true,  // Always allow viewing products
        canEditProducts: false, // PWA doesn't edit products
        canMatchTags: hasTagItems,  // Based on mobile "tag_items" menuCode
        canManageTags: canSeeManageTags,  // Show button if ANY manage permission exists
        canViewReports: false,  // Reports not available in PWA
        canManageUsers: false,  // User management not available in PWA
        // Granular permissions for EditTagModal
        canUpdateQuantity: hasUpdateQuantity,  // md_tags with edit action
        canChangeProduct: hasManageTags,       // mobile manage_tags
        canClearTagData: hasClearTagData,      // md_tags with delete action
        canReplaceTag: hasManageTags,          // mobile manage_tags
        // (2026-04-16, Jason Cox) Manual Checkout is Smart-only -- explicitly
        // disable for SWS users.
        canManualCheckout: false,
        // (2026-04-22, Jason Cox) Device Status is Smart-only.
        canViewDeviceStatus: false,
        // (2026-04-22, Jason Cox) Consumption Events + Restock Events
        // are Smart-only.
        canViewDeliveryTickets: false,
        canViewRestockEvents: false,
        // (2026-04-24, Jason Cox) Inventory is Smart-only.
        canViewInventory: false,
      };
    } catch (error) {
      console.error('Error parsing SWS permissions, falling back to role-based:', error);
    }
  }

  // Fallback to role-based permissions when permission data is unavailable
  return user ? getRolePermissions(user.role) : getRolePermissions('viewer');
};

export const usePermissions = (): RolePermissions => {
  const { user } = useAuth();
  return derivePermissions(user);
};